
import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface AdminRouteProps {
  children: ReactNode;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        // First check if user is authenticated
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          // User not logged in
          setIsLoggedIn(false);
          setIsLoading(false);
          return;
        }

        setIsLoggedIn(true);

        // Now check if user has admin role
        const { data: isAdminRole, error: roleError } = await supabase.rpc('has_role', {
          role: 'admin'
        });

        if (roleError) {
          console.error('Error checking admin role:', roleError);
          throw new Error(roleError.message);
        }

        if (!isAdminRole) {
          // User doesn't have admin role
          toast({
            title: "Access denied",
            description: "You don't have permission to access the admin area.",
            variant: "destructive",
          });
          setIsAdmin(false);
          setIsLoading(false);
          return;
        }

        // User is authenticated and has admin role
        setIsAdmin(true);
        setIsLoading(false);
      } catch (error: any) {
        console.error('Error checking admin access:', error);
        toast({
          title: "Error",
          description: error.message || "An error occurred while checking access.",
          variant: "destructive",
        });
        setIsLoading(false);
      }
    };

    checkAdminAccess();
  }, [navigate, toast]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // If not logged in, show login prompt
  if (isLoggedIn === false) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">Admin Access</CardTitle>
            <CardDescription className="text-center">
              Please log in to access the admin area
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-center text-muted-foreground">
              You need to be logged in with admin privileges to view this page.
            </p>
          </CardContent>
          <CardFooter>
            <div className="flex flex-col w-full gap-2">
              <Button className="w-full" asChild>
                <Link to="/login">Log in</Link>
              </Button>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/">Back to Home</Link>
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // If admin status is false (not null), redirect to home
  if (isAdmin === false) {
    return <Navigate to="/" />;
  }

  // Admin status is true, render children
  return <>{children}</>;
};

export default AdminRoute;
